import { defineStore } from 'pinia';
import { convertGoodsStates } from '@/constants/common';
import {
  TypeOptions,
  DesignOrderModeOptions,
} from '@/views/develop/edition/constant';
import { sexOptions } from '@/constants/goods';
import {
  getCategoryList,
  getRangeList,
  getSeasonList,
  getStaffList,
  queryBlandList,
  queryStoreHouseList,
  querySupplierList,
} from '@/api/goods';
import { getClientList } from '@/api/sample';
import { getDesignOrderSerialList } from '@/api/design';
import { getRoleList } from '@/api/role';
import { formatTime } from '@/utils/common';
import { isFunction } from 'lodash';
import { GetFlags, ListDataState } from './types';

// 缓存时长
const CACHE_DURATION = 5000;
// 校验数据是否过期
const checkDeadline = (obj: Recordable) => {
  const nowTimestamp = Date.now();
  const { cache, timestamp = nowTimestamp } = obj;
  // 未缓存或已过期
  if (!cache || nowTimestamp - timestamp > CACHE_DURATION) {
    obj.cache = false;
    return true;
  }
  return false;
};

// 获取列表数据
const useListDataStore = defineStore('listData', {
  state: (): ListDataState => ({
    // 人员列表
    userObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 角色列表
    roleObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 品牌
    brandObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 季节
    seasonObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 波段
    rangesObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 商品分类
    categoryObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 客户
    clientObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 加工厂
    supplierObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 供应商
    factoryObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 仓库
    storehouseObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 版次
    designOrderSerialObj: {
      cache: false,
      timestamp: null,
      list: [],
    },
    // 性别
    // sexOptionsList: sexOptions,
    // 大货状态
    // convertGoodsStatesList: convertGoodsStates,
    // 版单类型
    // TypeOptionsList: TypeOptions,
    // 打版方式
    // designOrderModeOptionsList: DesignOrderModeOptions,
  }),

  getters: {
    // 使用region内的方法，建议先调用getDataListByField
    // #region
    getUserById: (state) => {
      return (id: string | number) =>
        state.userObj.list.find((item: Recordable) => `${item.id}` === `${id}`);
    },
    getRoleById: (state) => {
      return (id: string | number) =>
        state.roleObj.list.find((item: Recordable) => `${item.id}` === `${id}`);
    },
    getBrandById: (state) => {
      return (id: string | number) =>
        state.brandObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getSeasonById: (state) => {
      return (id: string | number) =>
        state.seasonObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getRangesById: (state) => {
      return (id: string | number) =>
        state.rangesObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getCategoryById: (state) => {
      return (id: string | number) =>
        state.categoryObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getClientById: (state) => {
      return (id: string | number) =>
        state.clientObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getSupplierById: (state) => {
      return (id: string | number) =>
        state.supplierObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getFactoryById: (state) => {
      return (id: string | number) =>
        state.factoryObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getStorehouseById: (state) => {
      return (id: string | number) =>
        state.storehouseObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    getDesignOrderSerialById: (state) => {
      return (id: string | number) =>
        state.designOrderSerialObj.list.find(
          (item: Recordable) => `${item.id}` === `${id}`
        );
    },
    // #endregion
    // 性别
    getSexOptionById: () => {
      return (val: string | number) =>
        sexOptions.find((item: Recordable) => `${item.val}` === `${val}`);
    },
    // 大货状态
    getConvertGoodsStateById: () => {
      return (val: string | number) =>
        convertGoodsStates.find(
          (item: Recordable) => `${item.val}` === `${val}`
        );
    },
    // 版单类型
    getEditionTypeOptionById: () => {
      return (val: string | number) =>
        TypeOptions.find((item: Recordable) => `${item.val}` === `${val}`);
    },
    // 打版方式
    getDesignOrderModeOptionById: () => {
      return (val: string | number) =>
        DesignOrderModeOptions.find(
          (item: Recordable) => `${item.val}` === `${val}`
        );
    },
  },

  actions: {
    // 建议使用该方法前，先调用getDataListByField
    // 根据id数组获取对应的数据列表
    getDataListByFieldAndIds(field: string, idList: any) {
      if (!field || !Array.isArray(idList)) return [];
      const {
        getUserById,
        getRoleById,
        getBrandById,
        getSeasonById,
        getRangesById,
        getCategoryById,
        getClientById,
        getSupplierById,
        getSexOptionById,
        getFactoryById,
        getDesignOrderSerialById,
        getDesignOrderModeOptionById,
        getEditionTypeOptionById,
        getStorehouseById,
        getConvertGoodsStateById,
      } = this;
      let dataList: any[] = [];
      switch (field) {
        case 'BRAND':
          dataList = idList.map((id: any) => getBrandById(id));
          break;
        case 'ROLE':
          dataList = idList.map((id: any) => getRoleById(id));
          break;
        case 'SEASON':
          dataList = idList.map((id: any) => getSeasonById(id));
          break;
        case 'RANGES':
          dataList = idList.map((id: any) => getRangesById(id));
          break;
        case 'SEX':
          dataList = idList.map((id: any) => getSexOptionById(id));
          break;
        case 'GOODS_CATEGORY':
          dataList = idList.map((id: any) => getCategoryById(id));
          break;
        case 'USER':
          dataList = idList.map((id: any) => getUserById(id));
          break;
        case 'CLIENT':
          dataList = idList.map((id: any) => getClientById(id));
          break;
        case 'FACTORY':
          dataList = idList.map((id: any) => getFactoryById(id));
          break;
        case 'SUPPLIER':
          dataList = idList.map((id: any) => getSupplierById(id));
          break;
        case 'STOREHOUSE':
          dataList = idList.map((id: any) => getStorehouseById(id));
          break;
        case 'CONVERT_GOODS_STATE':
          dataList = idList.map((id: any) => getConvertGoodsStateById(id));
          break;
        case 'DESIGN_ORDER_SERIAL':
          dataList = idList.map((id: any) => getDesignOrderSerialById(id));
          break;
        case 'DESIGN_ORDER_TYPE':
          dataList = idList.map((id: any) => getEditionTypeOptionById(id));
          break;
        case 'DESIGN_ORDER_MODE':
          dataList = idList.map((id: any) => getDesignOrderModeOptionById(id));
          break;
        default:
          break;
      }
      // 过滤列表中不存在的项
      return dataList.filter((i: any) => i);
    },
    // 建议使用该方法前，先调用getDataListByField
    // 在getDataListByFieldAndIds的基础上补充其他信息
    getListDataOptionByFieldAndIds({
      field,
      idList = [],
      formatter,
    }: Recordable) {
      const option: Recordable = {
        datas: [],
        ids: [],
        labels: [],
        conditionJoinSymbol: '、', // 条件审批会用到的连接符
        valueField: 'id',
        labelField: 'name',
      };
      if (
        [
          'BRAND',
          'ROLE',
          'SEASON',
          'RANGES',
          'SEX',
          'GOODS_CATEGORY',
          'USER',
          'CLIENT',
          'FACTORY',
          'SUPPLIER',
          'STOREHOUSE',
          'CONVERT_GOODS_STATE',
          'DESIGN_ORDER_SERIAL',
          'DESIGN_ORDER_TYPE',
          'DESIGN_ORDER_MODE',
        ].includes(field)
      ) {
        option.datas = this.getDataListByFieldAndIds(field, idList);
      } else if (field === 'YEAR') {
        option.datas = idList.map((i: any) => ({
          id: i,
          name: isFunction(formatter) ? formatter(i) : `${i}年`,
        }));
      } else if (field === 'MONTH') {
        option.datas = idList.map((i: any) => ({ id: i, name: `${i}月` }));
      } else if (field === 'DATE') {
        option.datas = idList.map((i: any) => ({
          id: i,
          name: `${formatTime(i, 'YYYY-MM-DD')}日`,
        }));
      } else {
        option.datas = idList.map((i: any) => ({ id: i, name: String(i) }));
      }

      if (
        [
          'SEX',
          'CONVERT_GOODS_STATE',
          'DESIGN_ORDER_TYPE',
          'DESIGN_ORDER_MODE',
        ].includes(field)
      ) {
        option.labelField = 'text';
        option.valueField = 'val';
      } else if (['CLIENT'].includes(field)) {
        option.labelField = 'clientName';
      } else if (['FACTORY', 'SUPPLIER'].includes(field)) {
        option.labelField = 'supplierName';
      }

      if (['YEAR', 'MONTH', 'DATE', 'NUMBER'].includes(field)) {
        option.conditionJoinSymbol = '至';
      }

      option.ids = option.datas.map((i: Recordable) => i[option.valueField]);
      option.labels = option.datas.map(
        (i: Recordable) => i[option.labelField] ?? ''
      );
      return option;
    },
    // 获取相关数据列表
    async getDataListByField({
      USER,
      ROLE,
      BRAND,
      SEASON,
      RANGES,
      GOODS_CATEGORY, // 商品分类
      CLIENT,
      SUPPLIER,
      FACTORY,
      STOREHOUSE, // 仓库
      DESIGN_ORDER_SERIAL, // 设计款版次
    }: GetFlags) {
      const promiseList: any[] = [];
      const resultHandlers: Array<{
        fn: (data: Array<Recordable>) => void;
        name: string;
      }> = [];
      if (USER && checkDeadline(this.userObj)) {
        promiseList.push(getStaffList({}, { page: 1, limit: 9999 }));
        resultHandlers.push({ fn: this.setUsers, name: 'userObj' });
      }
      if (ROLE && checkDeadline(this.roleObj)) {
        promiseList.push(getRoleList({}, { page: 1, limit: 9999 }));
        resultHandlers.push({ fn: this.setRoles, name: 'roleObj' });
      }
      if (BRAND && checkDeadline(this.brandObj)) {
        promiseList.push(queryBlandList({}, { page: 1, limit: 9999 }));
        resultHandlers.push({ fn: this.setBrands, name: 'brandObj' });
      }
      if (SEASON && checkDeadline(this.seasonObj)) {
        promiseList.push(getSeasonList({ page: 1, limit: 9999 }, {}));
        resultHandlers.push({ fn: this.setSeasons, name: 'seasonObj' });
      }
      if (RANGES && checkDeadline(this.rangesObj)) {
        promiseList.push(getRangeList({ page: 1, limit: 9999 }, {}));
        resultHandlers.push({ fn: this.setRanges, name: 'rangesObj' });
      }
      if (GOODS_CATEGORY && checkDeadline(this.categoryObj)) {
        promiseList.push(getCategoryList({}, { page: 1, limit: 9999 }));
        resultHandlers.push({ fn: this.setCategorys, name: 'categoryObj' });
      }
      if (CLIENT && checkDeadline(this.clientObj)) {
        promiseList.push(getClientList({ page: 1, pageSize: 9999 }));
        resultHandlers.push({ fn: this.setClients, name: 'clientObj' });
      }
      if (SUPPLIER && checkDeadline(this.supplierObj)) {
        promiseList.push(
          querySupplierList({ supplierType: '0', page: 1, pageSize: 9999 })
        );
        resultHandlers.push({ fn: this.setSuppliers, name: 'supplierObj' });
      }
      if (FACTORY && checkDeadline(this.factoryObj)) {
        promiseList.push(
          querySupplierList({
            supplierType: '1',
            isFactory: 2,
            page: 1,
            pageSize: 9999,
          })
        );
        resultHandlers.push({ fn: this.setFactorys, name: 'factoryObj' });
      }
      if (STOREHOUSE && checkDeadline(this.storehouseObj)) {
        promiseList.push(queryStoreHouseList({}, { page: 1, limit: 9999 }));
        resultHandlers.push({
          fn: this.setStorehouses,
          name: 'storehouseObj',
        });
      }
      if (DESIGN_ORDER_SERIAL && checkDeadline(this.designOrderSerialObj)) {
        promiseList.push(
          getDesignOrderSerialList({ page: 1, limit: 9999 }, {})
        );
        resultHandlers.push({
          fn: this.setDesignOrderSerials,
          name: 'designOrderSerialObj',
        });
      }
      return Promise.allSettled(promiseList).then((results: any) => {
        results.forEach((result: any, index: number) => {
          if (result.status === 'fulfilled') {
            const handler = resultHandlers[index];
            handler.fn(result.value.data?.list || []);
            this[handler.name].cache = true;
            this[handler.name].timestamp = Date.now();
          } else {
            console.error(`${resultHandlers[index].name}:`, result.reason);
          }
        });
      });
    },
    setUsers(data: Array<Recordable>) {
      this.userObj.list = data || [];
      this.userObj.timestamp = Date.now();
    },
    setRoles(data: Array<Recordable>) {
      this.roleObj.list = data || [];
      this.roleObj.timestamp = Date.now();
    },
    setBrands(data: Array<Recordable>) {
      this.brandObj.list = data || [];
      this.brandObj.timestamp = Date.now();
    },
    setSeasons(data: Array<Recordable>) {
      this.seasonObj.list = data || [];
      this.seasonObj.timestamp = Date.now();
    },
    setRanges(data: Array<Recordable>) {
      this.rangesObj.list = data || [];
      this.rangesObj.timestamp = Date.now();
    },
    setCategorys(data: Array<Recordable>) {
      this.categoryObj.list = data || [];
      this.categoryObj.timestamp = Date.now();
    },
    setClients(data: Array<Recordable>) {
      this.clientObj.list = data || [];
      this.clientObj.timestamp = Date.now();
    },
    setSuppliers(data: Array<Recordable>) {
      this.supplierObj.list = data || [];
      this.supplierObj.timestamp = Date.now();
    },
    setFactorys(data: Array<Recordable>) {
      this.factoryObj.list = data || [];
      this.factoryObj.timestamp = Date.now();
    },
    setStorehouses(data: Array<Recordable>) {
      this.storehouseObj.list = data || [];
      this.storehouseObj.timestamp = Date.now();
    },
    setDesignOrderSerials(data: Array<Recordable>) {
      this.designOrderSerialObj.list = data || [];
      this.designOrderSerialObj.timestamp = Date.now();
    },
  },
});

export default useListDataStore;
